/**
 * Registration of complaints received by email, post or phone (requirements 7.2, 7.3).
 */
import { useState } from 'react';
import { apiClient, type ComplainantType, type ContentReport, type ExternalComplaintCreate, type ReportReason, type ReportSubjectType } from '../../lib/api-client';
import { jstLocalInputToIso, nowAsJstLocalInput } from '../../lib/date-utils';
import { CHANNEL_LABELS, COMPLAINANT_LABELS, REPORT_REASON_LABELS, SUBJECT_TYPE_LABELS } from './moderationLabels';

type ExternalChannel = ExternalComplaintCreate['channel'];

const EXTERNAL_CHANNELS: ExternalChannel[] = ['email', 'postal', 'phone', 'other'];

interface ExternalComplaintFormProps {
  onCreated: (report: ContentReport) => void | Promise<void>;
}

export default function ExternalComplaintForm({ onCreated }: ExternalComplaintFormProps) {
  const [receivedAt, setReceivedAt] = useState(nowAsJstLocalInput());
  const [channel, setChannel] = useState<ExternalChannel>('email');
  const [complainantType, setComplainantType] = useState<ComplainantType>('advocacy_org');
  const [complainantName, setComplainantName] = useState('');
  const [streamerUsername, setStreamerUsername] = useState('');
  const [subjectType, setSubjectType] = useState<ReportSubjectType>('other');
  const [reason, setReason] = useState<ReportReason>('prohibited_content');
  const [details, setDetails] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!details.trim()) {
      setError('内容を入力してください');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const report = await apiClient.createExternalComplaint({
        received_at: jstLocalInputToIso(receivedAt),
        channel,
        complainant_type: complainantType,
        complainant_name: complainantName.trim() || null,
        streamer_username: streamerUsername.trim() || null,
        subject_type: subjectType,
        reason,
        details: details.trim(),
      });
      await onCreated(report);
    } catch (err) {
      setError(err instanceof Error ? err.message : '登録に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-pink-300 bg-pink-50 p-4 text-sm" aria-label="外部の苦情を登録">
      <h3 className="mb-3 font-bold">メール・郵送・電話で受け付けた苦情を登録</h3>
      <div className="grid gap-3 md:grid-cols-2">
        <label>
          受付日時（日本時間）
          <input type="datetime-local" required value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} className="mt-1 block w-full border p-2" />
        </label>
        <label>
          受付経路
          <select value={channel} onChange={(e) => setChannel(e.target.value as ExternalChannel)} className="mt-1 block w-full border p-2">
            {EXTERNAL_CHANNELS.map((value) => <option key={value} value={value}>{CHANNEL_LABELS[value]}</option>)}
          </select>
        </label>
        <label>
          申立者の種別
          <select value={complainantType} onChange={(e) => setComplainantType(e.target.value as ComplainantType)} className="mt-1 block w-full border p-2">
            {(Object.keys(COMPLAINANT_LABELS) as ComplainantType[]).map((value) => (
              <option key={value} value={value}>{COMPLAINANT_LABELS[value]}</option>
            ))}
          </select>
        </label>
        <label>
          申立者名（団体名など）
          <input value={complainantName} onChange={(e) => setComplainantName(e.target.value)} maxLength={100} className="mt-1 block w-full border p-2" />
        </label>
        <label>
          対象の配信者（ユーザー名、任意）
          <input value={streamerUsername} onChange={(e) => setStreamerUsername(e.target.value)} maxLength={100} className="mt-1 block w-full border p-2" />
        </label>
        <label>
          対象の種類
          <select value={subjectType} onChange={(e) => setSubjectType(e.target.value as ReportSubjectType)} className="mt-1 block w-full border p-2">
            {(Object.keys(SUBJECT_TYPE_LABELS) as ReportSubjectType[]).map((value) => (
              <option key={value} value={value}>{SUBJECT_TYPE_LABELS[value]}</option>
            ))}
          </select>
        </label>
        <label>
          理由
          <select value={reason} onChange={(e) => setReason(e.target.value as ReportReason)} className="mt-1 block w-full border p-2">
            {(Object.keys(REPORT_REASON_LABELS) as ReportReason[]).map((value) => (
              <option key={value} value={value}>{REPORT_REASON_LABELS[value]}</option>
            ))}
          </select>
        </label>
        <label className="md:col-span-2">
          内容
          <textarea value={details} onChange={(e) => setDetails(e.target.value)} maxLength={2000} rows={4} className="mt-1 block w-full border p-2" />
        </label>
      </div>
      {error && <p className="mt-2 text-red-700" role="alert">{error}</p>}
      <div className="mt-3 flex justify-end">
        <button type="submit" disabled={saving} className="bg-pink-600 px-4 py-2 font-semibold text-white disabled:opacity-50">
          {saving ? '登録中...' : '登録して期限を設定'}
        </button>
      </div>
    </form>
  );
}
